/**
 * DocumentTranslationPanel
 * 
 * Inline chat component for translating uploaded documents.
 * Shows when user uploads documents and requests translation.
 * Supports single and batch (multiple file) translation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Stack,
    Text,
    Dropdown,
    IDropdownOption,
    PrimaryButton,
    DefaultButton,
    ProgressIndicator,
    MessageBar,
    MessageBarType,
    Icon,
    Link,
    Spinner,
    SpinnerSize,
    Checkbox,
    IconButton
} from '@fluentui/react';
import { authenticatedFetch } from '../../utils/authApi';
import { buildApiUrl } from '../../config/apiConfig';
import styles from './DocumentTranslationPanel.module.css';

// Language options for translation
const LANGUAGE_OPTIONS: IDropdownOption[] = [
    { key: 'he', text: 'Hebrew (עברית)' },
    { key: 'en', text: 'English' },
    { key: 'ar', text: 'Arabic (العربية)' },
    { key: 'fr', text: 'French (Français)' },
    { key: 'de', text: 'German (Deutsch)' },
    { key: 'es', text: 'Spanish (Español)' },
    { key: 'ru', text: 'Russian (Русский)' },
    { key: 'zh-Hans', text: 'Chinese Simplified (简体中文)' },
    { key: 'ja', text: 'Japanese (日本語)' },
    { key: 'pt', text: 'Portuguese (Português)' },
    { key: 'it', text: 'Italian (Italiano)' },
    { key: 'ko', text: 'Korean (한국어)' },
    { key: 'pl', text: 'Polish (Polski)' },
    { key: 'tr', text: 'Turkish (Türkçe)' },
    { key: 'uk', text: 'Ukrainian (Українська)' },
];

interface UploadedFile {
    id: string;
    name?: string;       // Local file name
    filename?: string;   // Backend filename
    file?: File;
    type?: string;
    size?: number;
}

// Helper to get file display name
const getFileName = (file: UploadedFile): string => {
    return file.filename || file.name || 'Unknown file';
};

interface TranslationResult {
    success: boolean;
    translated_document_url?: string;
    original_filename?: string;
    translated_filename?: string;
    source_language?: string;
    target_language?: string;
    characters_charged?: number;
    error?: string;
    job_id?: string;
}

interface BatchTranslationResult {
    success: boolean;
    results?: TranslationResult[];
    total_characters_charged?: number;
    successful_count?: number;
    failed_count?: number;
    job_id?: string;
    error?: string;
}

interface DocumentTranslationPanelProps {
    /** Uploaded files available for translation */
    uploadedFiles: UploadedFile[];
    /** Detected target language from user message (optional) */
    detectedLanguage?: string;
    /** Callback when translation completes */
    onTranslationComplete?: (result: TranslationResult | BatchTranslationResult) => void;
    /** Callback to dismiss the panel and continue with normal chat */
    onDismiss?: () => void;
    /** User's original message */
    userMessage?: string;
}

type TranslationStatus = 'idle' | 'uploading' | 'translating' | 'completed' | 'error';

export const DocumentTranslationPanel: React.FC<DocumentTranslationPanelProps> = ({
    uploadedFiles,
    detectedLanguage,
    onTranslationComplete,
    onDismiss,
    userMessage
}) => {
    // For single file, select that file. For multiple, select all by default
    const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>([]);
    const [targetLanguage, setTargetLanguage] = useState<string>(detectedLanguage || 'he');
    const [status, setStatus] = useState<TranslationStatus>('idle');
    const [progress, setProgress] = useState<number>(0);
    const [results, setResults] = useState<TranslationResult[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
    const [downloadedIndices, setDownloadedIndices] = useState<Set<number>>(new Set());
    const [jobId, setJobId] = useState<string | null>(null);
    
    // Word conversion state
    const [convertingToWordIndex, setConvertingToWordIndex] = useState<number | null>(null);
    const [wordConversionResults, setWordConversionResults] = useState<Map<number, { url: string; filename: string }>>(new Map());
    
    // Minimized state - allows users to return to download files
    const [isMinimized, setIsMinimized] = useState<boolean>(false);
    
    // Abort controller for cancelling translation
    const abortControllerRef = React.useRef<AbortController | null>(null);

    // Auto-select files: all files if multiple, single file if only one
    useEffect(() => {
        if (uploadedFiles.length > 0 && selectedFiles.length === 0) {
            setSelectedFiles([...uploadedFiles]);
        }
    }, [uploadedFiles, selectedFiles.length]);

    // Auto-detect language from user message
    useEffect(() => {
        if (detectedLanguage && detectedLanguage !== targetLanguage) {
            setTargetLanguage(detectedLanguage);
        }
    }, [detectedLanguage]);

    const toggleFileSelection = (file: UploadedFile) => {
        setSelectedFiles(prev => {
            const isSelected = prev.some(f => f.id === file.id);
            if (isSelected) {
                return prev.filter(f => f.id !== file.id);
            } else {
                return [...prev, file];
            }
        });
    };

    // Cancel translation in progress
    const handleCancelTranslation = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setStatus('idle');
        setProgress(0);
        setError('Translation cancelled');
        console.log('[DocumentTranslation] Translation cancelled by user');
    }, []);

    const handleTranslate = useCallback(async () => {
        if (selectedFiles.length === 0) {
            setError('Please select at least one file to translate');
            return;
        }

        // Create new abort controller
        abortControllerRef.current = new AbortController();

        setStatus('uploading');
        setProgress(0);
        setError(null);
        setResults([]);
        setDownloadedIndices(new Set());

        try {
            const formData = new FormData();
            formData.append('target_language', targetLanguage);

            // Check if we have actual File objects or need to use file_ids
            const hasFileObjects = selectedFiles.some(f => f.file);
            
            // Use batch endpoint for multiple files, single endpoint for one file
            const isBatch = selectedFiles.length > 1;
            
            if (hasFileObjects) {
                // Upload actual files - use correct field name based on batch/single
                for (const file of selectedFiles) {
                    if (file.file) {
                        // Single file endpoint expects 'file', batch expects 'files'
                        formData.append(isBatch ? 'files' : 'file', file.file);
                    }
                }
            } else {
                // Use file_ids for files already in storage
                const fileIds = selectedFiles.map(f => f.id);
                formData.append('file_ids', JSON.stringify(fileIds));
            }

            setStatus('translating');
            setProgress(30);

            const endpoint = isBatch ? '/api/translation/translate-batch' : '/api/translation/translate';

            // For single file with file_id, also include it separately
            if (!isBatch && !hasFileObjects && selectedFiles.length === 1) {
                formData.delete('file_ids');
                formData.append('file_id', selectedFiles[0].id);
            }

            const response = await authenticatedFetch(endpoint, {
                method: 'POST',
                body: formData,
                signal: abortControllerRef.current?.signal
            });

            setProgress(70);

            const data = await response.json();

            if (data.success) {
                setStatus('completed');
                setProgress(100);
                
                if (isBatch && data.results) {
                    // Batch response
                    setResults(data.results);
                    setJobId(data.job_id);
                    onTranslationComplete?.(data as BatchTranslationResult);
                } else {
                    // Single file response
                    const singleResult: TranslationResult = {
                        success: data.success,
                        translated_document_url: data.translated_document_url,
                        original_filename: data.original_filename,
                        translated_filename: data.translated_filename,
                        source_language: data.source_language,
                        target_language: data.target_language,
                        characters_charged: data.characters_charged,
                        job_id: data.job_id
                    };
                    setResults([singleResult]);
                    setJobId(data.job_id);
                    onTranslationComplete?.(singleResult);
                }
            } else {
                throw new Error(data.error || 'Translation failed');
            }
        } catch (err: any) {
            // Check if cancelled
            if (err.name === 'AbortError') {
                console.log('[DocumentTranslation] Translation aborted');
                return;
            }
            setStatus('error');
            setError(err.message || 'An error occurred during translation');
            console.error('[DocumentTranslation] Error:', err);
        } finally {
            abortControllerRef.current = null;
        }
    }, [selectedFiles, targetLanguage, onTranslationComplete]);

    const handleDownload = useCallback(async (index: number) => {
        const result = results[index];
        if (result?.translated_document_url) {
            setDownloadingIndex(index);
            
            try {
                const downloadUrl = result.translated_document_url.startsWith('http')
                    ? result.translated_document_url
                    : buildApiUrl(result.translated_document_url);
                
                // Use fetch + blob approach for reliable cross-origin downloads
                const response = await fetch(downloadUrl);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = result.translated_filename || result.original_filename || 'translated_document';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
                
                setDownloadedIndices(prev => new Set([...prev, index]));
            } catch (err) {
                console.error('[DocumentTranslation] Download error:', err);
                // Fallback to window.open if fetch fails
                const downloadUrl = result.translated_document_url.startsWith('http')
                    ? result.translated_document_url
                    : buildApiUrl(result.translated_document_url);
                window.open(downloadUrl, '_blank');
                setDownloadedIndices(prev => new Set([...prev, index]));
            } finally {
                setDownloadingIndex(null);
            }
        }
    }, [results]);

    const handleDownloadAll = useCallback(async () => {
        for (let i = 0; i < results.length; i++) {
            if (results[i]?.translated_document_url) {
                await handleDownload(i);
                // Small delay between downloads
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }
    }, [results, handleDownload]);

    // Convert PDF to Word
    const handleConvertToWord = useCallback(async (index: number) => {
        const result = results[index];
        if (!result?.translated_document_url || !result.translated_filename?.toLowerCase().endsWith('.pdf')) {
            return;
        }

        setConvertingToWordIndex(index);

        try {
            const response = await authenticatedFetch('/api/translation/convert-to-word', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_url: result.translated_document_url,
                    filename: result.translated_filename,
                    target_language: targetLanguage  // Pass for RTL support
                })
            });

            const data = await response.json();

            if (data.success && data.word_document_url) {
                // Store the Word conversion result
                setWordConversionResults(prev => {
                    const newMap = new Map(prev);
                    newMap.set(index, {
                        url: data.word_document_url,
                        filename: data.word_filename
                    });
                    return newMap;
                });

                // Trigger download of Word document
                const link = document.createElement('a');
                link.href = data.word_document_url;
                link.download = data.word_filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                setError(data.error || 'Word conversion failed');
            }
        } catch (err: any) {
            console.error('[DocumentTranslation] Word conversion error:', err);
            setError(err.message || 'Word conversion failed');
        } finally {
            setConvertingToWordIndex(null);
        }
    }, [results, targetLanguage]);

    // Check if file is a PDF
    const isPdfFile = (filename: string | undefined): boolean => {
        return !!filename && filename.toLowerCase().endsWith('.pdf');
    };

    const handleMinimize = useCallback(() => {
        setIsMinimized(true);
    }, []);
    
    const handleRestore = useCallback(() => {
        setIsMinimized(false);
    }, []);
    
    const handleFullDismiss = useCallback(async () => {
        if (jobId) {
            try {
                await authenticatedFetch('/api/translation/cleanup', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ job_id: jobId })
                });
                console.log('[DocumentTranslation] Cleanup completed for job:', jobId);
            } catch (err) {
                console.warn('[DocumentTranslation] Cleanup failed:', err);
            }
        }
        onDismiss?.();
    }, [jobId, onDismiss]);

    // Consider all downloaded only when PDFs have both versions (original + Word) downloaded
    const allDownloaded = results.length > 0 && results.every((result, index) => {
        const pdfDownloaded = downloadedIndices.has(index);
        const isPdf = isPdfFile(result.translated_filename);
        // For PDFs, also require Word conversion to be done
        if (isPdf) {
            return pdfDownloaded && wordConversionResults.has(index);
        }
        return pdfDownloaded;
    });

    const getStatusIcon = () => {
        switch (status) {
            case 'uploading':
            case 'translating':
                return <Spinner size={SpinnerSize.small} />;
            case 'completed':
                return <Icon iconName="CheckMark" className={styles.successIcon} />;
            case 'error':
                return <Icon iconName="ErrorBadge" className={styles.errorIcon} />;
            default:
                return <Icon iconName="LocaleLanguage" className={styles.translationIcon} />;
        }
    };

    const getStatusText = () => {
        const fileCount = selectedFiles.length;
        switch (status) {
            case 'uploading':
                return `Uploading ${fileCount} document${fileCount > 1 ? 's' : ''}...`;
            case 'translating':
                return `Translating ${fileCount} document${fileCount > 1 ? 's' : ''}... This may take a few minutes.`;
            case 'completed':
                return `Translation complete! ${results.length} document${results.length > 1 ? 's' : ''} ready.`;
            case 'error':
                return 'Translation failed';
            default:
                return `Ready to translate ${fileCount} document${fileCount > 1 ? 's' : ''}`;
        }
    };

    // Minimized view - compact bar to restore full panel
    if (isMinimized && status === 'completed' && results.length > 0) {
        return (
            <div 
                className={styles.minimizedBar}
                onClick={handleRestore}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                    backgroundColor: '#107c10',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    marginBottom: '8px'
                }}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Icon iconName="CheckMark" />
                    <Text style={{ color: 'white', fontWeight: 500 }}>
                        {results.length} translated document{results.length > 1 ? 's' : ''} ready
                    </Text>
                    <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                        ({downloadedIndices.size}/{results.length} downloaded)
                    </Text>
                </Stack>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <DefaultButton
                        text="View Downloads"
                        iconProps={{ iconName: 'Download' }}
                        onClick={(e) => { e.stopPropagation(); handleRestore(); }}
                        styles={{ root: { backgroundColor: 'white', color: '#107c10' } }}
                    />
                    <Icon 
                        iconName="Cancel" 
                        onClick={(e) => { e.stopPropagation(); handleFullDismiss(); }}
                        style={{ cursor: 'pointer', opacity: 0.8 }}
                        title="Close and dismiss"
                    />
                </Stack>
            </div>
        );
    }

    return (
        <div className={styles.panelContainer}>
            <div className={styles.panelHeader}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    {getStatusIcon()}
                    <Text variant="mediumPlus" className={styles.headerTitle}>
                        Document Translation {uploadedFiles.length > 1 ? `(${selectedFiles.length}/${uploadedFiles.length})` : ''}
                    </Text>
                </Stack>
                {onDismiss && status === 'idle' && (
                    <DefaultButton
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={onDismiss}
                        className={styles.dismissButton}
                        title="Use AI for text translation instead"
                        text="Use AI Instead"
                    />
                )}
            </div>

            <div className={styles.panelContent}>
                {/* Status message */}
                <Text className={styles.statusText}>{getStatusText()}</Text>

                {/* Error message */}
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                        className={styles.messageBar}
                    >
                        {error}
                    </MessageBar>
                )}

                {/* File selection - show checkboxes for multiple files */}
                {status === 'idle' && uploadedFiles.length > 1 && (
                    <div className={styles.fileSelectionContainer}>
                        <Text className={styles.sectionLabel}>Select documents to translate:</Text>
                        <Stack tokens={{ childrenGap: 8 }} className={styles.fileCheckboxList}>
                            {uploadedFiles.map(file => (
                                <Checkbox
                                    key={file.id}
                                    label={getFileName(file)}
                                    checked={selectedFiles.some(f => f.id === file.id)}
                                    onChange={() => toggleFileSelection(file)}
                                    className={styles.fileCheckbox}
                                />
                            ))}
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 8 }} className={styles.selectAllButtons}>
                            <Link onClick={() => setSelectedFiles([...uploadedFiles])}>Select All</Link>
                            <Link onClick={() => setSelectedFiles([])}>Clear All</Link>
                        </Stack>
                    </div>
                )}

                {/* Single file display */}
                {status === 'idle' && uploadedFiles.length === 1 && selectedFiles.length === 1 && (
                    <div className={styles.selectedFile}>
                        <Icon iconName="Document" className={styles.fileIcon} />
                        <Text className={styles.fileName}>{getFileName(selectedFiles[0])}</Text>
                    </div>
                )}

                {/* Language selection */}
                {status === 'idle' && (
                    <Dropdown
                        label="Translate to"
                        selectedKey={targetLanguage}
                        onChange={(_, option) => setTargetLanguage(option?.key as string)}
                        options={LANGUAGE_OPTIONS}
                        className={styles.dropdown}
                    />
                )}

                {/* Progress indicator with Cancel button */}
                {(status === 'uploading' || status === 'translating') && (
                    <Stack tokens={{ childrenGap: 12 }}>
                        <ProgressIndicator
                            label={status === 'uploading' ? 'Uploading...' : 'Translating...'}
                            percentComplete={progress / 100}
                            className={styles.progressIndicator}
                        />
                        <DefaultButton
                            text="Cancel Translation"
                            iconProps={{ iconName: 'Cancel' }}
                            onClick={handleCancelTranslation}
                            styles={{ 
                                root: { 
                                    backgroundColor: '#fff',
                                    borderColor: '#d32f2f',
                                    color: '#d32f2f'
                                },
                                rootHovered: {
                                    backgroundColor: '#ffebee',
                                    borderColor: '#d32f2f',
                                    color: '#d32f2f'
                                }
                            }}
                        />
                    </Stack>
                )}

                {/* Success result - Multiple files */}
                {status === 'completed' && results.length > 0 && (
                    <div className={styles.resultContainer}>
                        {!allDownloaded ? (
                            <>
                                {/* Compact success header with action icons */}
                                <div className={styles.successHeaderCompact}>
                                    <div className={styles.successHeaderLeft}>
                                        <Icon iconName="CheckMark" className={styles.successCheckIconSmall} />
                                        <Text className={styles.successTitleCompact}>
                                            Translation Complete
                                        </Text>
                                    </div>
                                    <div className={styles.headerActions}>
                                        <IconButton
                                            iconProps={{ iconName: 'ChromeMinimize' }}
                                            className={styles.headerIconButton}
                                            onClick={handleMinimize}
                                            title="Minimize"
                                            ariaLabel="Minimize panel"
                                        />
                                        <IconButton
                                            iconProps={{ iconName: 'ChromeClose' }}
                                            className={styles.headerIconButton}
                                            onClick={handleFullDismiss}
                                            title="Close"
                                            ariaLabel="Close panel"
                                        />
                                    </div>
                                </div>
                                
                                {/* Document list - compact cards */}
                                <div className={styles.documentsReadyList}>
                                    {results.map((result, index) => (
                                        <div key={index} className={styles.documentCardCompact}>
                                            <div className={styles.documentCardLeft}>
                                                <Icon iconName="DocumentReply" className={styles.documentIconSmall} />
                                                <div className={styles.documentInfoCompact}>
                                                    <Text className={styles.documentNameCompact}>
                                                        {result.translated_filename || result.original_filename}
                                                    </Text>
                                                    <Text className={styles.languageTag}>
                                                        {result.target_language?.toUpperCase()}
                                                    </Text>
                                                </div>
                                            </div>
                                            <div className={styles.documentActionsCompact}>
                                                {/* Download icon button */}
                                                {downloadingIndex === index ? (
                                                    <Spinner size={SpinnerSize.small} />
                                                ) : downloadedIndices.has(index) ? (
                                                    <Icon iconName="CheckMark" className={styles.downloadedCheckIcon} />
                                                ) : (
                                                    <IconButton
                                                        iconProps={{ iconName: isPdfFile(result.translated_filename) ? 'PDF' : 'Download' }}
                                                        onClick={() => handleDownload(index)}
                                                        className={styles.actionIconButton}
                                                        title={`Download ${isPdfFile(result.translated_filename) ? 'PDF' : 'file'}`}
                                                        ariaLabel="Download"
                                                    />
                                                )}
                                                
                                                {/* Word conversion icon - only for PDFs */}
                                                {isPdfFile(result.translated_filename) && (
                                                    wordConversionResults.has(index) ? (
                                                        <IconButton
                                                            iconProps={{ iconName: 'WordDocument' }}
                                                            onClick={() => {
                                                                const wordResult = wordConversionResults.get(index);
                                                                if (wordResult) {
                                                                    const link = document.createElement('a');
                                                                    link.href = wordResult.url;
                                                                    link.download = wordResult.filename;
                                                                    document.body.appendChild(link);
                                                                    link.click();
                                                                    document.body.removeChild(link);
                                                                }
                                                            }}
                                                            className={styles.wordIconButton}
                                                            title="Download Word"
                                                            ariaLabel="Download Word version"
                                                        />
                                                    ) : convertingToWordIndex === index ? (
                                                        <Spinner size={SpinnerSize.small} />
                                                    ) : (
                                                        <IconButton
                                                            iconProps={{ iconName: 'WordDocument' }}
                                                            onClick={() => handleConvertToWord(index)}
                                                            className={styles.actionIconButton}
                                                            title="Convert to Word"
                                                            ariaLabel="Convert to Word"
                                                        />
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Download All - only for multiple files */}
                                {results.length > 1 && (
                                    <button
                                        className={styles.downloadAllLink}
                                        onClick={handleDownloadAll}
                                        disabled={allDownloaded}
                                    >
                                        <Icon iconName="CloudDownload" />
                                        <span>Download All ({results.length})</span>
                                    </button>
                                )}
                            </>
                        ) : (
                            /* All downloads complete state */
                            <div className={styles.downloadCompleteCompact}>
                                <Icon iconName="CompletedSolid" className={styles.completedIconSmall} />
                                <Text className={styles.completedText}>
                                    {results.length > 1 ? 'All downloads started!' : 'Download started!'}
                                </Text>
                                <IconButton
                                    iconProps={{ iconName: 'ChromeClose' }}
                                    className={styles.headerIconButton}
                                    onClick={handleFullDismiss}
                                    title="Close"
                                    ariaLabel="Close panel"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Action buttons */}
                {status === 'idle' && (
                    <Stack horizontal tokens={{ childrenGap: 8 }} className={styles.actionButtons}>
                        <PrimaryButton
                            iconProps={{ iconName: 'Translate' }}
                            text={selectedFiles.length > 1 ? `Translate ${selectedFiles.length} Documents` : 'Translate Document'}
                            onClick={handleTranslate}
                            disabled={selectedFiles.length === 0}
                        />
                    </Stack>
                )}

                {/* Try again button on error */}
                {status === 'error' && (
                    <Stack horizontal tokens={{ childrenGap: 8 }} className={styles.actionButtons}>
                        <PrimaryButton
                            iconProps={{ iconName: 'Refresh' }}
                            text="Try Again"
                            onClick={() => {
                                setStatus('idle');
                                setError(null);
                                setResults([]);
                            }}
                        />
                        {onDismiss && (
                            <DefaultButton
                                text="Cancel"
                                onClick={onDismiss}
                            />
                        )}
                    </Stack>
                )}
            </div>

            <div className={styles.panelFooter}>
                <Text variant="xSmall" className={styles.footerText}>
                    Powered by Azure AI Document Translation • Preserves original formatting
                </Text>
            </div>
        </div>
    );
};

export default DocumentTranslationPanel;
